import { useState } from 'react';
import cn from 'classnames';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { RealtimeEditor } from '@components/editors/realtime-editor/body/realtime-editor.tsx';

type RealtimeSegment = {
  id: number;
  text: string;
  offset_ms: number;
  words: [string, number, number, number][];
  is_final: boolean;
};

type Props = {
  index: number;
  segment: RealtimeSegment;
};

export const RealtimeEditorRow = ({ index, segment }: Props) => {
  const [isActive, setIsActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { theme } = useThemeContext();

  // Theme-aware colors
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const activeBorderColor = pick('rgb(191 219 254)', 'rgb(59 130 246)'); // blue-200 / blue-500
  const activeBgColor = pick('rgb(239 246 255)', 'rgba(30, 58, 138, 0.2)'); // blue-50 / blue-900/20
  const hoverBorderColor = pick('rgb(229 231 235)', 'rgb(55 65 81)'); // gray-200 / gray-700
  const hoverBgColor = pick('rgb(249 250 251)', 'rgba(31, 41, 55, 0.3)'); // gray-50 / gray-800/30

  const handleRowClick = () => {
    setIsActive(true);
  };

  const getStyles = () => {
    const textColor = pick('rgb(0 0 0)', 'rgb(255 255 255)'); // black / white

    if (isActive) {
      return {
        borderColor: activeBorderColor,
        backgroundColor: activeBgColor,
        color: textColor
      };
    }
    if (isHovered) {
      return {
        borderColor: hoverBorderColor,
        backgroundColor: hoverBgColor,
        color: textColor
      };
    }
    return {
      borderColor: 'transparent',
      backgroundColor: 'transparent',
      color: textColor
    };
  };

  return (
    <div
      onClick={handleRowClick}
      onFocus={handleRowClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'rounded-2xl transition-all duration-200 cursor-pointer',
        'border p-3',
        isActive && 'shadow-md'
      )}
      style={getStyles()}
    >
      <RealtimeEditor index={index} segment={segment} />
    </div>
  );
};
